import React from 'react';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Link } from '@uhg-abyss/web/ui/Link';
import { event } from '@uhg-abyss/web/tools/event';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { getFeature } from '../../../../../redux/tools/features';

const StyledLink = styled(Link, {
  '&.abyss-link-root': {
    lineHeight: '20px',
  },
});
const Column = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 2,
  justifyContent: 'center',
  '@media screen (min-width: 985px)': {
    padding: '10px 0px',
  },
});
const Row = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

const CategoryLabel = styled(Text, {
  '&.abyss-text-root': {
    fontSize: '12px',
    fontWeight: 700,
    color: '$gray6',
    lineHeight: '14px',
  },
});

const Subtitle = styled(Text, {
  '&.abyss-text-root': {
    fontSize: '14px',
    lineHeight: '20px',
  },
});

const BiggerFontBadge = styled(Badge, {
  fontSize: '16px',
});

const SectionHeight = styled(Card.Section, {
  display: 'flex',
});

const StyledIconBrand = styled(IconBrand, {
  display: 'flex',
  marginRight: '5px',
});

const checkSubtitleDisplay = (section) => {
  const showSubtitle =
    section.subtitle && section.alwaysShowSubtitle
      ? true
      : section.badgeCount > 0;

  return showSubtitle;
};

const handleClick = (section) => {
  event('ACTION_REQUIRED_ACTION_ITEMS_CLICK', {
    interactionValue: section.badgeLabel,
    interactionContext: section.label,
  });
};

export const ActionRequiredCard = ({ sections }) => {
  return (
    <If condition={sections.length > 0}>
      <div>
        <Card size='small'>
          <For each='section' of={sections}>
            <If condition={section.toggle ? getFeature(section.toggle) : true}>
              <SectionHeight>
                <Row>
                  <div>
                    <Column>
                      <If condition={section.category}>
                        <CategoryLabel>{section.category}</CategoryLabel>
                      </If>
                      <StyledLink
                        onClick={() => {
                          handleClick(section);
                        }}
                        fontWeight='bold'
                        href={section.href}
                      >
                        {section.label}
                      </StyledLink>
                      <If condition={checkSubtitleDisplay(section)}>
                        <Subtitle color={section.subtitleVariant || '$gray8'}>
                          {section.subtitle}
                        </Subtitle>
                      </If>
                    </Column>
                  </div>
                  <div>
                    <Column>
                      <If condition={!section.success}>
                        <Row>
                          <StyledIconBrand
                            title='alert'
                            icon='alert'
                            isScreenReadable
                            size={16}
                          />{' '}
                          Unable to load
                        </Row>
                      </If>
                      <If condition={section.success}>
                        <Choose>
                          <When condition={section.displayLabelOnly}>
                            <BiggerFontBadge variant={section.badgeVariant}>
                              {section.badgeLabel}
                            </BiggerFontBadge>
                          </When>
                          <Otherwise>
                            <BiggerFontBadge variant={section.badgeVariant}>
                              {section.badgeCount} {section.badgeLabel}
                            </BiggerFontBadge>
                          </Otherwise>
                        </Choose>
                      </If>
                    </Column>
                  </div>
                </Row>
              </SectionHeight>
            </If>
          </For>
        </Card>
      </div>
    </If>
  );
};

ActionRequiredCard.propTypes = {
  sections: {
    alwaysShowSubtitle: PropTypes.bool,
    badgeCount: PropTypes.number,
    badgeLabel: PropTypes.string,
    badgeVariant: PropTypes.string,
    category: PropTypes.string,
    href: PropTypes.string,
    label: PropTypes.string,
    subtitle: PropTypes.string,
    subtitleVariant: PropTypes.string,
    toggle: PropTypes.string,
  },
};

ActionRequiredCard.defaultProps = {
  sections: {
    alwaysShowSubtitle: true,
    badgeCount: 0,
    badgeLabel: 'Require Action',
    badgeVariant: '',
    category: null,
    href: '/',
    label: '',
    subtitle: null,
    subtitleVariant: null,
    toggle: null,
  },
};
